import { darkTheme, lightTheme, type GlobalTheme, type GlobalThemeOverrides } from 'naive-ui';
import gaiaLightOverrides from './gaia-light.json';
import gaiaDarkOverrides from './gaia-dark.json';

export type DesignThemeKey = 'naive-light' | 'naive-dark' | 'gaia-light' | 'gaia-dark';

/**
 * A design theme is a Naive base theme plus an overrides JSON. The JSON files
 * are what the theme editor on /dev/design exports — refine a theme there,
 * export, and paste the result into the matching gaia-*.json.
 */
export interface DesignTheme {
  base: GlobalTheme;
  overrides: GlobalThemeOverrides;
}

export const designThemes: Record<DesignThemeKey, DesignTheme> = {
  'naive-light': { base: lightTheme, overrides: {} },
  'naive-dark': { base: darkTheme, overrides: {} },
  'gaia-light': { base: lightTheme, overrides: gaiaLightOverrides },
  'gaia-dark': { base: darkTheme, overrides: gaiaDarkOverrides },
};

export function isDesignThemeKey(value: unknown): value is DesignThemeKey {
  return typeof value === 'string' && value in designThemes;
}

/**
 * Merge theme-editor edits onto existing overrides. Overrides are one level
 * deep (section → vars: `common`, `Button`, …), so a per-section spread is a
 * faithful merge; whole vars within an edited section win from `edits`.
 */
export function mergeThemeOverrides(
  base: GlobalThemeOverrides,
  edits: GlobalThemeOverrides
): GlobalThemeOverrides {
  const merged: Record<string, unknown> = { ...base };
  for (const [section, vars] of Object.entries(edits)) {
    merged[section] = { ...(merged[section] as Record<string, unknown> | undefined), ...vars };
  }
  return merged;
}
